'use client';

import clsx from 'clsx';
import {
  AriaAttributes,
  JSX,
  Key,
  PropsWithChildren,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useInView } from 'react-intersection-observer';
import useDidChange from '@ui/useDidChange';

import styles from './DallasList.module.scss';

const OnLastItemInViewContext = createContext<
  (() => Promise<void> | void) | null
>(null);

function useOnLastItemInView() {
  return useContext(OnLastItemInViewContext);
}

function DEFAULT_GET_DATA_ITEM_KEY<T>(_: T, index: number): Key {
  return index;
}

export interface ListItemTemplateProps<T> {
  dataItem: T;
  last: boolean;
}

interface Props<T> {
  className?: string;
  listItemTemplate: (props: ListItemTemplateProps<T>) => JSX.Element;
  data: T[];
  /**
   * Optional function to getting the component key prop value used by react
   * in order to keep track of the state of the components.
   *
   * This function should be a REFERENCE THAT DOESN'T CHANGE
   * so, declare your function outside of the component you are using `<DallasTable />` in.
   */
  getDataItemKey?: (dataItem: T, index: number) => Key;
  /**
   * Callback function to be called when the last item is in view.
   * Make sure it's reference doesn't change.
   */
  onLastItemInView?: () => Promise<void> | void;
  hasMoreItems?: boolean;
  loadingIndicator?: React.ReactNode;
}

export function DallasList<T>({
  className,
  listItemTemplate: ListItemTemplate,
  data,
  getDataItemKey = DEFAULT_GET_DATA_ITEM_KEY<T>,
  onLastItemInView,
  hasMoreItems,
  loadingIndicator,
}: Props<T>) {
  const listItems = useMemo(
    () =>
      data.map((dataItem, i) => (
        <ListItemTemplate
          key={getDataItemKey(dataItem, i)}
          dataItem={dataItem}
          last={i === data.length - 1}
        />
      )),
    [data, getDataItemKey, ListItemTemplate],
  );

  return (
    <OnLastItemInViewContext.Provider value={onLastItemInView || null}>
      <div className={clsx(className, styles.root)}>
        {listItems}
        {hasMoreItems === true && loadingIndicator}
      </div>
    </OnLastItemInViewContext.Provider>
  );
}

interface ListItemProps extends AriaAttributes {
  className?: string;
  onClick?: () => void;
}

export const DallasListItem = forwardRef<
  HTMLTableRowElement,
  PropsWithChildren<ListItemProps>
>(function DallasListItem({ className, children, onClick, ...ariaProps }, ref) {
  return (
    <div
      {...ariaProps}
      ref={ref}
      className={clsx(className, styles.listItem, onClick && styles.clickable)}
      onClick={onClick}
    >
      {children}
    </div>
  );
});

interface VirtualListItemProps extends ListItemProps {
  last: boolean;
}

export function DallasVirtualListItem({
  className,
  children,
  last: isLast,
  onClick,
  ...ariaProps
}: PropsWithChildren<VirtualListItemProps>) {
  const [ref, isInView] = useInView();
  const onLastItemInView = useOnLastItemInView();
  const isLastInView = isLast && isInView;
  const didIsLastInViewChange = useDidChange(isLastInView);

  useEffect(() => {
    if (!didIsLastInViewChange && isLastInView) {
      onLastItemInView?.();
    }
  }, [didIsLastInViewChange, isLastInView, onLastItemInView]);

  return (
    <DallasListItem
      {...ariaProps}
      ref={ref}
      className={className}
      onClick={isInView ? onClick : undefined}
    >
      {isInView && children}
    </DallasListItem>
  );
}
